import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ROICalculator = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [formData, setFormData] = useState({
    employees: 50,
    monthlyRevenue: 2000000,
    productionVolume: 100
  });
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const translations = {
    en: {
      title: "Calculate Your ROI",
      subtitle: "See how much you can save with our ERP system tailored for aluminum manufacturers",
      form: {
        employees: "Number of Employees",
        monthlyRevenue: "Monthly Revenue (₹)",
        productionVolume: "Monthly Production Volume (Tons)",
        calculate: "Calculate Savings"
      },
      results: {
        title: "Your Potential Savings",
        monthlySavings: "Monthly Savings",
        annualSavings: "Annual Savings",
        roiPeriod: "ROI Payback Period",
        breakdown: "Savings Breakdown",
        laborCost: "Labor Cost Reduction",
        qualityImprovement: "Quality Improvement",
        efficiencyGains: "Efficiency Gains",
        inventoryOptimization: "Inventory Optimization"
      },
      cta: "Get Detailed ROI Report"
    },
    hi: {
      title: "अपना ROI गणना करें",
      subtitle: "देखें कि एल्यूमीनियम निर्माताओं के लिए तैयार हमारे ERP सिस्टम से आप कितना बचा सकते हैं",
      form: {
        employees: "कर्मचारियों की संख्या",
        monthlyRevenue: "मासिक राजस्व (₹)",
        productionVolume: "मासिक उत्पादन मात्रा (टन)",
        calculate: "बचत की गणना करें"
      },
      results: {
        title: "आपकी संभावित बचत",
        monthlySavings: "मासिक बचत",
        annualSavings: "वार्षिक बचत",
        roiPeriod: "ROI वापसी अवधि",
        breakdown: "बचत विवरण",
        laborCost: "श्रम लागत में कमी",
        qualityImprovement: "गुणवत्ता सुधार",
        efficiencyGains: "दक्षता लाभ",
        inventoryOptimization: "इन्वेंटरी अनुकूलन"
      },
      cta: "विस्तृत ROI रिपोर्ट प्राप्त करें"
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Show default statistics on mount
  useEffect(() => {
    if (results === null) {
      const { employees, monthlyRevenue, productionVolume } = formData;
      const laborCostReduction = employees * 8000;
      const qualityImprovement = monthlyRevenue * 0.03;
      const efficiencyGains = productionVolume * 1200;
      const inventoryOptimization = monthlyRevenue * 0.02;
      const totalMonthlySavings = laborCostReduction + qualityImprovement + efficiencyGains + inventoryOptimization;
      const annualSavings = totalMonthlySavings * 12;
      const implementationCost = 500000;
      const roiMonths = Math.ceil(implementationCost / totalMonthlySavings);
      setResults({
        monthlySavings: totalMonthlySavings,
        annualSavings: annualSavings,
        roiPeriod: roiMonths,
        breakdown: {
          laborCost: laborCostReduction,
          qualityImprovement: qualityImprovement,
          efficiencyGains: efficiencyGains,
          inventoryOptimization: inventoryOptimization
        }
      });
    }
  }, [formData, results]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: parseInt(value) || 0
    }));
  };

  const calculateROI = () => {
    setIsCalculating(true);
    
    // Simulate calculation delay
    setTimeout(() => {
      const { employees, monthlyRevenue, productionVolume } = formData;
      
      // ROI calculation logic based on industry benchmarks
      const laborCostReduction = employees * 8000; // ₹8,000 per employee monthly
      const qualityImprovement = monthlyRevenue * 0.03; // 3% of revenue
      const efficiencyGains = productionVolume * 1200; // ₹1,200 per ton
      const inventoryOptimization = monthlyRevenue * 0.02; // 2% of revenue
      
      const totalMonthlySavings = laborCostReduction + qualityImprovement + efficiencyGains + inventoryOptimization;
      const annualSavings = totalMonthlySavings * 12;
      const implementationCost = 500000; // Base implementation cost
      const roiMonths = Math.ceil(implementationCost / totalMonthlySavings);

      setResults({
        monthlySavings: totalMonthlySavings,
        annualSavings: annualSavings,
        roiPeriod: roiMonths,
        breakdown: {
          laborCost: laborCostReduction,
          qualityImprovement: qualityImprovement,
          efficiencyGains: efficiencyGains,
          inventoryOptimization: inventoryOptimization
        }
      });
      
      setIsCalculating(false);
    }, 2000);
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else {
      return `₹${amount.toLocaleString()}`;
    }
  };

  return (
    <section id="roi-calculator" className="py-20 bg-surface-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Calculator Form */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <Icon name="Calculator" size={24} color="var(--color-primary)" />
                <h3 className="text-xl font-semibold text-primary">
                  {currentLanguage === 'en' ? 'Business Information' : 'व्यावसायिक जानकारी'}
                </h3>
              </div>

              {/* Employees Input */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  {t.form.employees}
                </label>
                <Input
                  type="number"
                  value={formData.employees}
                  onChange={(e) => handleInputChange('employees', e.target.value)}
                  placeholder="50"
                  className="w-full"
                />
                <div className="mt-2">
                  <input
                    type="range"
                    min="10"
                    max="500"
                    value={formData.employees}
                    onChange={(e) => handleInputChange('employees', e.target.value)}
                    className="w-full h-2 bg-surface-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-text-muted mt-1">
                    <span>10</span>
                    <span>500</span>
                  </div>
                </div>
              </div>

              {/* Monthly Revenue Input */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  {t.form.monthlyRevenue}
                </label>
                <Input
                  type="number"
                  value={formData.monthlyRevenue}
                  onChange={(e) => handleInputChange('monthlyRevenue', e.target.value)}
                  placeholder="2000000"
                  className="w-full"
                />
                <div className="mt-2">
                  <input
                    type="range"
                    min="500000"
                    max="50000000"
                    step="100000"
                    value={formData.monthlyRevenue}
                    onChange={(e) => handleInputChange('monthlyRevenue', e.target.value)}
                    className="w-full h-2 bg-surface-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-text-muted mt-1">
                    <span>₹5L</span>
                    <span>₹5Cr</span>
                  </div>
                </div>
              </div>

              {/* Production Volume Input */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  {t.form.productionVolume}
                </label>
                <Input
                  type="number"
                  value={formData.productionVolume}
                  onChange={(e) => handleInputChange('productionVolume', e.target.value)}
                  placeholder="100"
                  className="w-full"
                />
                <div className="mt-2">
                  <input
                    type="range"
                    min="10"
                    max="1000"
                    value={formData.productionVolume}
                    onChange={(e) => handleInputChange('productionVolume', e.target.value)}
                    className="w-full h-2 bg-surface-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-text-muted mt-1">
                    <span>10</span>
                    <span>1000</span>
                  </div>
                </div>
              </div>

              {/* Calculate Button */}
              <Button
                variant="primary"
                size="lg"
                onClick={calculateROI}
                loading={isCalculating}
                className="w-full bg-gradient-accent hover:bg-accent-600 shadow-cta"
                iconName="Calculator"
                iconPosition="left"
              >
                {t.form.calculate}
              </Button>
            </div>
          </div>

          {/* Results Display */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
            {!results && !isCalculating ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Icon name="BarChart3" size={64} color="var(--color-text-muted)" className="mx-auto mb-4" />
                  <p className="text-text-muted">
                    {currentLanguage === 'en' ?'Enter your business details to see potential savings' :'संभावित बचत देखने के लिए अपने व्यावसायिक विवरण दर्ज करें'
                    }
                  </p>
                </div>
              </div>
            ) : isCalculating ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-text-secondary">
                    {currentLanguage === 'en' ? 'Calculating your savings...' : 'आपकी बचत की गणना की जा रही है...'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center space-x-3 mb-6">
                  <Icon name="TrendingUp" size={24} color="var(--color-success)" />
                  <h3 className="text-xl font-semibold text-primary">
                    {t.results.title}
                  </h3>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-success-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-success mb-1">
                      {formatCurrency(results.monthlySavings)}
                    </div>
                    <div className="text-sm text-success-600">
                      {t.results.monthlySavings}
                    </div>
                  </div>
                  
                  <div className="bg-primary-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-primary mb-1">
                      {formatCurrency(results.annualSavings)}
                    </div>
                    <div className="text-sm text-primary-600">
                      {t.results.annualSavings}
                    </div>
                  </div>
                </div>

                {/* ROI Period */}
                <div className="bg-accent-50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-accent mb-1">
                    {results.roiPeriod} {currentLanguage === 'en' ? 'months' : 'महीने'}
                  </div>
                  <div className="text-sm text-accent-600">
                    {t.results.roiPeriod}
                  </div>
                </div>

                {/* Savings Breakdown */}
                <div>
                  <h4 className="text-lg font-semibold text-primary mb-4">
                    {t.results.breakdown}
                  </h4>
                  <div className="space-y-3">
                    {Object.entries(results.breakdown).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-text-secondary text-sm">
                          {t.results[key]}
                        </span>
                        <span className="font-medium text-primary">
                          {formatCurrency(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full bg-gradient-primary hover:bg-primary-700"
                  iconName="FileText"
                  iconPosition="left"
                >
                  {t.cta}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;